import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  Observable,
  Subject,
  map,
  takeUntil,
  timer,
  Subscription,
  SchedulerLike,
  filter,
  tap,
} from 'rxjs';

import { TicketPrice, Video } from '@yoimo/interfaces';
import { VideoWithDocId } from '@yoimo/client-sdk/videos';
import { AsyncScheduler } from 'rxjs/internal/scheduler/AsyncScheduler';
import { PlatformService, ScopeService } from '@core/services';
import { HOUR, MILLISECOND, MINUTE } from '@core/utilities/utils';

export interface TimeDescriptor {
  value: string;
  unitLong: string;
  unitShort: string;
}
export interface Times {
  hours: TimeDescriptor;
  minutes: TimeDescriptor;
  seconds: TimeDescriptor;
}

@Component({
  selector: 'joymo-video-state',
  templateUrl: './video-state.component.html',
  styleUrls: ['./video-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoStateComponent implements OnInit, OnDestroy {
  readonly isServer: boolean;
  readonly stateScheduler: SchedulerLike = {
    now: () => {
      return this.scheduler.now();
    },
    schedule: <T>(
      ...args: [(state?: T) => void, number | undefined, T]
    ): Subscription => {
      return this.ngZone.runOutsideAngular(() => {
        return this.scheduler.schedule(...args);
      });
    },
  };

  @ViewChild('progress', { static: false }) progressBar!: ElementRef;

  @Output() getAccessClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() streamStartingSoon: EventEmitter<boolean> = new EventEmitter();
  @Input() hasAccess = false;
  @Input() video!: Video | VideoWithDocId;
  @Input() cheapestAccess?: TicketPrice;
  @Input() loading: boolean = false;
  @Input() isAccessInactive = false;
  timer$?: Observable<string>;
  scheduledTimer$?: Observable<TimeDescriptor[]>;

  startingSoon: boolean = false;
  cheapestAccessLabel?: string;
  homepageUrl = '';

  private readonly unsubscribe = new Subject<void>();

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private ngZone: NgZone,
    private scheduler: AsyncScheduler,
    private cdr: ChangeDetectorRef,
    private platformService: PlatformService,
    private scopeService: ScopeService
  ) {
    this.isServer = this.platformService.isServer();
  }

  ngOnInit(): void {
    if (this.isServer) {
      return;
    }

    this.homepageUrl = this.scopeService.getHomepageUrl();
    this.cheapestAccessLabel = this.video.unlocked
      ? $localize`:@@videoCheapestPriceFree:FREE`
      : $localize`:@@videoCheapestPriceStartingFrom:Starting from ${this.getCheapestPrice()}:price:`;

    if (
      this.video.type == 'STREAM' &&
      this.video.streamState &&
      this.video.streamState != 'FINISHED'
    ) {
      // Create a shared clock
      const clock$ = timer(0, MILLISECOND, this.stateScheduler).pipe(
        tap(() => setTimeout(() => this.cdr.detectChanges())),
        takeUntil(this.unsubscribe)
      );
      // Create obs for both
      this.scheduledTimer$ = this.getScheduledTimer$(clock$);

      if (this.video.streamState == 'LIVE') {
        this.timer$ = this.getTimer$(clock$);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  getCheapestPrice(): string {
    if (!this.cheapestAccess) {
      return '';
    }
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.cheapestAccess.currency,
    }).format(this.cheapestAccess.price);
  }

  /**
   * @returns A string representing streaming hours for today.
   * @exemple "Today 17:00–19:00"
   */
  getCountdownDate(): string {
    const rtf = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' });
    let startDate = this.video.startTime?.getDate() || 0;
    let dateDiff = startDate - new Date().getDate();
    return `${rtf.format(dateDiff >= 1 ? 1 : 0, 'day')} ${this.getTimeLabel()}`;
  }

  /**
   * @returns An array representing the countdown between now and the video start time.
   */
  getCountdownTimes(): TimeDescriptor[] {
    return this.getTimesToDisplay(
      this.getTimes(this.video.startTime!.getTime() - new Date().getTime())
    ) as TimeDescriptor[];
  }

  /**
   * @returns duration of video in miliiseconds
   */
  getDurationInMs(): number {
    switch (this.video.type) {
      case 'STREAM':
        return this.video.endTime!.getTime() - this.video.startTime!.getTime();
      default:
        return this.video.duration || 0;
    }
  }

  getMsSinceStart(): number {
    return this.video.startTime
      ? new Date().getTime() - this.video.startTime.getTime()
      : 0;
  }

  getWithin24Hours(): boolean {
    if (!this.video.startTime) {
      return false;
    }

    let diffInMs = Math.abs(
      this.video.startTime.getTime() - new Date().getTime()
    );

    return diffInMs >= 0 && diffInMs <= 24 * HOUR;
  }

  getReadableDuration(): string {
    const times = this.getTimesToDisplay(
      this.getTimes(
        this.video.duration ? this.video.duration * MILLISECOND : 0
      ),
      undefined
    ) as TimeDescriptor[];

    if (times.length == 3) {
      times.pop();
    }

    return times.map((td) => `${td.value} ${td.unitShort}`).join(' ');
  }

  getTimer$(clock$: Observable<number>): Observable<string> {
    return clock$.pipe(
      map((_) => {
        let progress = (this.getMsSinceStart() / this.getDurationInMs()) * 100;
        this.getUpdateProgress(`${Math.min(progress, 100)}%`);

        let times = this.getTimes(
          this.getMsSinceStart() >= this.getDurationInMs()
            ? this.getDurationInMs()
            : this.getMsSinceStart()
        );
        return this.getTimesToDisplay(times, ':') as string;
      })
    );
  }

  getScheduledTimer$(clock$: Observable<number>): Observable<TimeDescriptor[]> {
    return clock$.pipe(
      filter(
        (_) =>
          this.getWithin24Hours() &&
          this.video.streamState != 'LIVE' &&
          !this.startingSoon
      ),
      map((_) => {
        return this.getCountdownTimes();
      }),
      tap((descriptor) => {
        if (
          (descriptor.length < 3 && parseInt(descriptor[0].value) < 1) ||
          this.video?.startTime! < new Date()
        ) {
          this.setStreamStartingSoon();
        }
      })
    );
  }
  setStreamStartingSoon() {
    this.startingSoon = true;
    //if (this.hasAccess) {
    //this might be needed to silence the case where we don't know if a a user has access or not.or if we don't have access to the event yet
    this.streamStartingSoon.next(true);
    //}
  }

  getTimeLabel(): string {
    let startHour = this.video.startTime
      ?.getHours()
      .toString()
      .padStart(2, '0');
    let startMin = this.video.startTime
      ?.getMinutes()
      .toString()
      .padStart(2, '0');
    let endHour = this.video.endTime?.getHours().toString().padStart(2, '0');
    let endMin = this.video.endTime?.getMinutes().toString().padStart(2, '0');

    return `${startHour}:${startMin}–${endHour}:${endMin}`;
  }

  /**
   * @param diffInMs milliseconds
   * @returns Object representing hours, minutes & seconds and their unit
   */
  getTimes(diffInMs: number): Times {
    diffInMs = Math.round(diffInMs / MILLISECOND) * MILLISECOND;
    const hours = Math.floor(diffInMs / HOUR);
    const minutes = Math.floor((diffInMs % HOUR) / MINUTE);
    const seconds = Math.floor(((diffInMs % HOUR) % MINUTE) / MILLISECOND);

    return {
      hours: {
        value: Math.max(0, hours).toString(),
        unitLong: 'hours',
        unitShort: 'h',
      },
      minutes: {
        value: Math.max(0, minutes).toString(),
        unitLong: 'minutes',
        unitShort: 'min',
      },
      seconds: {
        value: Math.max(0, seconds).toString(),
        unitLong: 'seconds',
        unitShort: 'sec',
      },
    };
  }

  /**
   * @returns A static version of the timer showing how far in the stream it is.
   * @exemple "1:35:16"
   */
  getTimeSinceStart(): string | never {
    if (!this.video.startTime) {
      throw new Error('this video does not have a start time');
    }

    // Separator was provided  so it will be a string
    return this.getTimesToDisplay(
      this.getTimes(this.getMsSinceStart()),
      ':'
    ) as string;
  }

  /**
   * @param times
   * @optional separator Separator between times to be displayed
   * @returns Displayable version of Times
   */
  getTimesToDisplay(
    times: Times,
    separator?: string
  ): string | TimeDescriptor[] {
    let displayedTimes = Object.values(times);
    if (times.hours.value == '0') {
      displayedTimes.shift();
    }

    displayedTimes = displayedTimes.map((time, index) => {
      let t = { ...time };
      t.value = t.value.toString();
      if (index !== 0) {
        t.value = t.value.padStart(2, '0');
      }
      return t;
    });

    return separator
      ? displayedTimes.map((t) => t.value).join(separator)
      : displayedTimes;
  }

  getTimeUntilEnd() {
    if (!this.video.endTime || !this.video.startTime) {
      throw new Error('this video does not have an end time');
    }
    return this.getTimesToDisplay(this.getTimes(this.getDurationInMs()), ':');
  }

  getUpdateProgress(percent: string): void {
    this.progressBar.nativeElement.style.setProperty('width', percent);
  }
}
